import { useCallback, useEffect, useState } from "react";
import { RefreshCw, LogOut, Users, MailCheck, Link2 } from "lucide-react";
import { toast } from "sonner";
import { eventConfig } from "@/config/eventConfig";
import { adminLogin, fetchRegistrations, clearRegistrations, clearTraffic, deleteRegistration, deleteRegistrationsMany, restoreRegistrations, resendReminders } from "@/lib/registrationService";
import { DashboardLogin } from "@/components/dashboard/DashboardLogin";
import { StatCard } from "@/components/dashboard/DashboardStats";
import { TrafficPanel } from "@/components/dashboard/TrafficPanel";
import { AdviserChart, AdviserLeaderboard } from "@/components/dashboard/AdviserPanels";
import { RegistrationsTable } from "@/components/dashboard/RegistrationsTable";
import { AdviserLinks } from "@/components/dashboard/AdviserLinks";
import { VideoShareCard } from "@/components/dashboard/VideoShareCard";
import { useDashboardMetrics } from "@/components/dashboard/useDashboardMetrics";
import { toCSV, downloadFile } from "@/components/dashboard/dashboardUtils";
import { readAdminSession, writeAdminSession, clearAdminSession, msUntilExpiry } from "@/lib/adminSession";

// Only an opaque, short-lived session token is kept in the browser (never the
// password). It is issued by /api/admin/login, expires server-side, and the
// client mirrors that expiry (see lib/adminSession.js for the storage review).
const HEADER_BUTTON_CLASS =
  "inline-flex items-center gap-2 h-10 px-3 rounded-xl border border-white/15 bg-white/5 text-sm hover:bg-white/10 transition-colors";

export default function Dashboard() {
  const [password, setPassword] = useState("");
  const [session, setSession] = useState(() => readAdminSession());
  const token = session?.token || "";
  const [authed, setAuthed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [query, setQuery] = useState("");
  const [clearing, setClearing] = useState(false);
  const [clearingTraffic, setClearingTraffic] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [resending, setResending] = useState(false);

  const logout = useCallback(() => {
    clearAdminSession();
    setSession(null);
    setAuthed(false);
    setData(null);
    setPassword("");
  }, []);

  // Fetch dashboard data with a session token. On 401 the session is dropped.
  const load = useCallback(
    async (sessionToken) => {
      if (!sessionToken) return;
      setLoading(true);
      try {
        setData(await fetchRegistrations(sessionToken, eventConfig.eventId));
        setAuthed(true);
      } catch (err) {
        if (err?.response?.status === 401) {
          toast.error("Session expired \u2014 please log in again.");
          logout();
        } else {
          toast.error("Could not load registrations. Please try again.");
        }
      } finally {
        setLoading(false);
      }
    },
    [logout]
  );

  // Exchange the password for a token, then load. Password is never stored.
  const login = async (pw) => {
    setLoading(true);
    try {
      const issued = await adminLogin(pw);
      writeAdminSession(issued);
      setSession({ token: issued.token, expiresAt: issued.expires_at || null });
      setPassword("");
      await load(issued.token);
    } catch (err) {
      toast.error(err?.response?.status === 401 ? "Incorrect password." : "Could not log in. Please try again.");
      setLoading(false);
    }
  };

  useEffect(() => {
    document.title = "Registrations Dashboard | Affinity Group";
  }, []);

  // Resume an existing session on mount / after token changes.
  useEffect(() => {
    if (token && !authed) load(token);
  }, [token, authed, load]);

  // Auto-logout when the mirrored server expiry is reached so a stale token is
  // never left usable in the tab.
  useEffect(() => {
    const ms = msUntilExpiry(session?.expiresAt);
    if (!token || ms === null) return undefined;
    const id = setTimeout(() => {
      toast.info("Session expired \u2014 please log in again.");
      logout();
    }, ms);
    return () => clearTimeout(id);
  }, [token, session, logout]);

  const runAdminAction = async (action, setBusy, successMessage, failureMessage) => {
    setBusy(true);
    try {
      const res = await action(token, eventConfig.eventId);
      toast.success(successMessage(res));
      await load(token);
    } catch (err) {
      if (err?.response?.status === 401) {
        toast.error("Session expired \u2014 please log in again.");
        logout();
      } else {
        toast.error(failureMessage);
      }
    } finally {
      setBusy(false);
    }
  };

  const clearAll = () =>
    runAdminAction(
      clearRegistrations,
      setClearing,
      (res) => `Cleared ${res.deleted} registration${res.deleted === 1 ? "" : "s"}.`,
      "Could not clear registrations. Please try again."
    );

  const clearAllTraffic = () =>
    runAdminAction(
      clearTraffic,
      setClearingTraffic,
      (res) => `Reset traffic data (${res.deleted} visit${res.deleted === 1 ? "" : "s"} cleared).`,
      "Could not reset traffic data. Please try again."
    );

  // Restore a just-deleted batch (undo) within the server's undo window.
  const undoDelete = async (trashId) => {
    try {
      const res = await restoreRegistrations(token, trashId);
      toast.success(`Restored ${res.restored} registration${res.restored === 1 ? "" : "s"}.`);
      await load(token);
    } catch (err) {
      if (err?.response?.status === 401) {
        toast.error("Session expired \u2014 please log in again.");
        logout();
      } else {
        toast.error("Undo window expired \u2014 could not restore.");
      }
    }
  };

  // Success toast with an inline Undo action after any delete.
  const notifyDeleted = (res) => {
    const count = res?.deleted ?? 0;
    const label = `Deleted ${count} registration${count === 1 ? "" : "s"}.`;
    if (res?.trash_id) {
      toast.success(label, {
        duration: 8000,
        action: { label: "Undo", onClick: () => undoDelete(res.trash_id) },
      });
    } else {
      toast.success(label);
    }
  };

  const handleDeleteError = (err) => {
    if (err?.response?.status === 401) {
      toast.error("Session expired \u2014 please log in again.");
      logout();
    } else {
      toast.error("Could not delete registration. Please try again.");
    }
  };

  // Delete a single registration (used to remove test entries).
  const deleteOne = async (regId) => {
    setDeletingId(regId);
    try {
      notifyDeleted(await deleteRegistration(token, regId, eventConfig.eventId));
      await load(token);
    } catch (err) {
      handleDeleteError(err);
    } finally {
      setDeletingId(null);
    }
  };

  // Bulk-delete every ticked registration in one action.
  const deleteSelected = async () => {
    const ids = Array.from(selectedIds);
    if (!ids.length) return;
    setBulkDeleting(true);
    try {
      const res = await deleteRegistrationsMany(token, ids, eventConfig.eventId);
      setSelectedIds(new Set());
      notifyDeleted(res);
      await load(token);
    } catch (err) {
      handleDeleteError(err);
    } finally {
      setBulkDeleting(false);
    }
  };

  const toggleSelect = (id) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const toggleSelectAll = (ids, checked) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      ids.forEach((id) => (checked ? next.add(id) : next.delete(id)));
      return next;
    });

  const downloadCSV = (rows, filenamePrefix) => {
    const blob = new Blob(["\ufeff" + toCSV(rows)], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    downloadFile(url, `${filenamePrefix}-${new Date().toISOString().slice(0, 10)}.csv`);
    URL.revokeObjectURL(url);
  };

  const exportCSV = () => {
    if (!data?.items?.length) {
      toast.info("No registrations to export yet.");
      return;
    }
    downloadCSV(data.items, "cpf-registrations");
    toast.success("CSV exported.");
  };

  // Export only the ticked registrations to a CSV.
  const exportSelected = () => {
    const rows = (data?.items || []).filter((it) => selectedIds.has(it.id));
    if (!rows.length) {
      toast.info("No registrations selected.");
      return;
    }
    downloadCSV(rows, "cpf-registrations-selected");
    toast.success(`Exported ${rows.length} selected registration${rows.length === 1 ? "" : "s"}.`);
  };

  // Resend the seminar reminder email to every ticked registrant.
  const resendSelected = async (milestone) => {
    const ids = Array.from(selectedIds);
    if (!ids.length) return;
    setResending(true);
    try {
      const res = await resendReminders(token, ids, milestone, eventConfig.eventId);
      toast.success(
        `Reminder sent to ${res.sent} of ${res.attempted} registrant${res.attempted === 1 ? "" : "s"}.`
      );
    } catch (err) {
      if (err?.response?.status === 401) {
        toast.error("Session expired \u2014 please log in again.");
        logout();
      } else {
        toast.error("Could not send reminders. Please try again.");
      }
    } finally {
      setResending(false);
    }
  };

  const metrics = useDashboardMetrics(data, query);
  const selectedItems = (data?.items || []).filter((it) => selectedIds.has(it.id));

  if (!authed) {
    return <DashboardLogin password={password} onPasswordChange={setPassword} onSubmit={login} loading={loading} />;
  }

  return (
    <div className="min-h-screen bg-[#FBF8F0]">
      <header className="sticky top-0 z-30 bg-[#071524] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={eventConfig.brand.logo} alt="Affinity Group" className="h-8 w-auto" />
            <span className="hidden sm:block text-sm text-white/70">Registrations Dashboard</span>
          </div>
          <div className="flex items-center gap-2">
            <button data-testid="dashboard-refresh" onClick={() => load(token)} className={HEADER_BUTTON_CLASS}>
              <RefreshCw size={16} className={loading ? "animate-spin" : ""} /> Refresh
            </button>
            <button data-testid="dashboard-logout" onClick={logout} className={HEADER_BUTTON_CLASS}>
              <LogOut size={16} /> Logout
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <StatCard icon={Users} label="Total registrations" value={metrics.total} />
          <StatCard icon={MailCheck} label="Marketing opt-ins" value={metrics.consentCount} />
          <StatCard icon={Link2} label="Active adviser links" value={(eventConfig.advisers || []).length} />
        </div>

        <TrafficPanel
          rows={metrics.activeTrafficRows}
          totals={metrics.trafficTotals}
          bestPerformer={metrics.bestPerformer}
          dailyTrend={metrics.dailyTrend}
          loading={loading}
          clearing={clearingTraffic}
          onReset={clearAllTraffic}
        />

        <AdviserChart data={metrics.topAdvisers} />
        <AdviserLeaderboard rows={metrics.leaderboard} total={metrics.total} />

        <RegistrationsTable
          items={metrics.filteredItems}
          total={metrics.total}
          query={query}
          onQueryChange={setQuery}
          onExport={exportCSV}
          onClear={clearAll}
          onDelete={deleteOne}
          deletingId={deletingId}
          selectedIds={selectedIds}
          onToggleSelect={toggleSelect}
          onToggleSelectAll={toggleSelectAll}
          onDeleteSelected={deleteSelected}
          bulkDeleting={bulkDeleting}
          onExportSelected={exportSelected}
          onResendSelected={resendSelected}
          resending={resending}
          selectedItems={selectedItems}
          loading={loading}
          clearing={clearing}
        />

        <VideoShareCard />
        <AdviserLinks referralCount={metrics.referralCount} />
      </div>
    </div>
  );
}
